<template>
  <div class="dropdown btn-group b-dropdown" v-bind:class="{ show: visible }" v-on:keydown="keydown">
    <button ref="toggle"
      type="button"
      v-bind:id="`${id}-toggle`"
      v-bind:class="toggleClasses"
      aria-haspopup="true"
      v-bind:aria-expanded="visible ? 'true' : 'false'"
      v-on:click="toggle">{{text}}</button>
    <ul ref="menu"
      role="menu"
      tabindex="-1"
      v-bind:class="menuClasses"
      v-bind:aria-labelledby="`${id}-toggle`">
      <slot></slot>
    </ul>
  </div>
</template>

<script>
// Remplace le <b-dropdown> de bootstrap-vue 2 : même balisage, menu placé par Popper sous le bouton
// (aligné à droite avec « right »), fermé par un clic ailleurs, Échap ou le choix d'un élément.
import { createPopper } from '@popperjs/core';

let count = 0;

export default {
  name: 'BsDropdown',
  provide() {
    return { bsDropdown: this };
  },
  props: {
    text: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      required: false,
    },
    variant: {
      type: String,
      default: 'secondary',
    },
    right: {
      type: Boolean,
      default: false,
    },
    offset: {
      type: [Number, String],
      default: 0,
    },
    noCaret: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    count += 1;
    return {
      id: `bs-dropdown-${count}`,
      visible: false,
    };
  },
  computed: {
    toggleClasses() {
      return {
        btn: true,
        [`btn-${this.variant}`]: Boolean(this.variant),
        [`btn-${this.size}`]: Boolean(this.size),
        'dropdown-toggle': true,
        'dropdown-toggle-no-caret': this.noCaret,
      };
    },
    menuClasses() {
      return {
        'dropdown-menu': true,
        'dropdown-menu-end': this.right,
        show: this.visible,
      };
    },
  },
  beforeUnmount() {
    this.visible = false;
    this.removeListeners();
    this.destroyPopper();
  },
  methods: {
    toggle() {
      if (this.visible) {
        this.hide();
      } else {
        this.show();
      }
    },
    show() {
      this.visible = true;
      document.addEventListener('click', this.clickOutside);
      document.addEventListener('focusin', this.clickOutside);
      this.$nextTick(() => {
        if (this.visible && !this.popper) {
          this.popper = createPopper(this.$refs.toggle, this.$refs.menu, {
            placement: this.right ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: [Number(this.offset) || 0, 2] } }],
          });
        }
      });
    },
    hide(refocus) {
      if (!this.visible) {
        return;
      }
      this.visible = false;
      this.removeListeners();
      this.destroyPopper();
      if (refocus && this.$refs.toggle) {
        this.$refs.toggle.focus();
      }
    },
    clickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.hide();
      }
    },
    keydown(event) {
      if (event.key === 'Escape' && this.visible) {
        event.preventDefault();
        this.hide(true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.visible) {
          this.show();
        }
        this.focusItem(event.key === 'ArrowDown' ? 1 : -1);
      }
    },
    focusItem(step) {
      const items = [...this.$refs.menu.querySelectorAll('.dropdown-item:not(:disabled)')];
      if (items.length === 0) {
        return;
      }
      const index = items.indexOf(document.activeElement);
      const next = index < 0 ? (step > 0 ? 0 : items.length - 1)
        : Math.min(Math.max(index + step, 0), items.length - 1);
      items[next].focus();
    },
    removeListeners() {
      document.removeEventListener('click', this.clickOutside);
      document.removeEventListener('focusin', this.clickOutside);
    },
    destroyPopper() {
      if (this.popper) {
        this.popper.destroy();
        this.popper = undefined;
      }
    },
  },
};
</script>
